<template>
  <div class="mt-3">
    <h4>Total {{ total_items }}</h4>
    <div style="display: flex;justify-content: end;">
      <v-btn
        v-if="checkedRows.length > 0"
        color="red"
        dark
        class="mb-3"
        @click="showDeleteDialog = true"
      >
        <v-icon left>delete</v-icon> ({{ checkedRows.length }})
      </v-btn>
    </div>
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete <b>{{ checkedRows.length }}</b> selected campaign(s)?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="red" dark @click="deleteSelectedCampaigns">
            Yes, Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-data-table
      no-results-text="Loading... Please wait"
      :headers="headersWithCheckbox"
      :items="campaign_list"
      :pagination.sync="pagination"
      :loading="loaders.campaign_list_loader"
      :total-items="total_items"
      :rows-per-page-items="[10, 20, 50, 100]"
      class="elevation-1"
    >
      <template v-slot:headers="props">
        <tr>
          <th>
            <v-checkbox
              hide-details
              :input-value="allSelected"
              @change="toggleAll"
              :disabled="activeRows.length === 0"
            ></v-checkbox>
          </th>

          <th v-for="header in props.headers" :key="header.text">
            {{ header.text }}
          </th>
        </tr>
      </template>

      <template v-slot:items="props">
        <tr>
          <td style="display:flex; align-items:center; gap:8px;">
            <v-checkbox
              v-model="checkedRows"
              :value="String(props.item.id)"
              :disabled="props.item.status == 0 || !props.item.id"
              hide-details
            ></v-checkbox>
          </td>
          <td>
            <v-btn small icon color="primary" @click="copyCampaign(props.item)" title="Edit Campaign">
              <v-icon small>edit</v-icon>
            </v-btn>
          </td>
          <td>
            <v-chip :color="getStatusColor(props.item)" dark>
              {{ getStatusLabel(props.item) }}
            </v-chip>
          </td>
          <td>{{ props.item.name }}</td>
          <td>{{ props.item.title }}</td>
          <td>{{ props.item.description }}</td>
          <td>{{ props.item.campaign_image_url }}</td>
          <td>{{ props.item.campaign_type }}</td>
          <td>{{ props.item.campaign_type_url }}</td>
          <td>{{ props.item.start_date }}</td>
          <td>{{ props.item.end_date }}</td>
          <td>{{ props.item.display_interval_hours }}</td>
          <td>{{ props.item.ios_deeplink_url }}</td>
          <td>{{ props.item.android_deeplink_url }}</td>
          <td>{{ props.item.terms_cond_url }}</td>
          <!-- <td>{{ props.item.steps }}</td> -->
        </tr>
      </template>

      <template v-slot:no-data>
        <v-layout v-if="loaders.campaign_list_loader" justify-center>
          Loading... Please wait
        </v-layout>
        <v-layout v-else justify-center>
          No data available
        </v-layout>
      </template>
    </v-data-table>
    <v-snackbar :color="snackBar.color" :top="snackBar.top" :bottom="snackBar.bottom" :left="snackBar.left"
      :right="snackBar.right" v-model="snackBar.show">
      {{ snackBar.message }}
      <v-btn dark flat @click.native="snackBar.show = false">
        <v-icon>close</v-icon>
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import axios from "axios";

export default {
  props: {
    reload: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    
    return {
      current_date: new Date().toISOString().substr(0, 10),

      campaign_list: [],
      total_items: 0,

      checkedRows: [],
      showDeleteDialog: false,

      filters: {
        status: null,
      },

      pagination: {
        page: 1,
        rowsPerPage: 10,
        sortBy: "",
        descending: true,
      },

      loaders: {
        campaign_list_loader: false,
      },

      snackBar: {
        show: false,
        message: "",
        color: "",
        top: true,
        right: true,
      },

      headers: [
        { text: "Action", value: "action", sortable: false },
        { text: "Status", value: "status" },
        { text: "Name", value: "name" },
        { text: "Title", value: "title" },
        { text: "Description", value: "description" },
        { text: "Campaign Image URL ", value: "campaign_image_url" },
        { text: "Campaign Type ", value: "campaign_type" },
        { text: "Campaign Type URL", value: "campaign_type_url" },
        { text: "Start Date", value: "start_date" },
        { text: "End Date", value: "end_date" },
        { text: "Display Interval Hours", value: "display_interval_hours" },
        { text: "iOS Deeplink URL", value: "ios_deeplink_url" },
        { text: "Android Deeplink URL", value: "android_deeplink_url" },
        { text: "Terms & Conditions URL", value: "terms_cond_url" },
        // { text: "Steps", value: "steps" },
      ],
    };
  },

  computed: {
    activeRows() {
      return this.campaign_list.filter(item => item.status == 1);
    },
    allSelected() {
      const activeRows = this.campaign_list.filter(item => item.status == 1);

      return (
        activeRows.length > 0 &&
        this.checkedRows.length === activeRows.length
      );
    },
    headersWithCheckbox() {
      return this.headers.filter(h => !h.hidden);
    },
  },

  watch: {
    pagination: {
      handler() {
        this.getCampaignList();
      },
      deep: true,
    },
    reload() {
      this.getCampaignList();
    },
  },

  mounted() {
    this.getCampaignList();
  },

  methods: {
    applyFilters() {
      this.pagination.page = 1;
      this.getCampaignList();
    },

    getCampaignList() {
      this.loaders.campaign_list_loader = true;

      axios
        .post(
          `${process.env.VUE_APP_API_URL}/api/v4/get-campaign-list`,
          {},
          {
            headers: this.$headers,
            params: {
              page: this.pagination.page,
              rowsPerPage: this.pagination.rowsPerPage,
              sortBy: this.pagination.sortBy,
              descending: this.pagination.descending,
              status:
                this.filters.status === null
                  ? undefined
                  : this.filters.status,
            },
          }
        )
        .then((response) => {
          if (response.data.status == 1) {
            const payload = response.data || {};
            const paginatedData = payload.data || {};
            const list = Array.isArray(paginatedData.data)
              ? paginatedData.data
              : [];

            this.campaign_list = list.map((item) => this.normalizeCampaignItem(item));

            this.total_items =
              paginatedData.total !== undefined
                ? Number(paginatedData.total)
                : payload.total !== undefined
                  ? Number(payload.total)
                  : this.campaign_list.length;

            if (
              paginatedData.current_page &&
              Number(paginatedData.current_page) !== this.pagination.page
            ) {
              this.pagination.page = Number(paginatedData.current_page);
            }
            // Clear checked rows on data refresh
            this.checkedRows = [];
          }
        })
        .catch(() => {
          this.$toast && this.$toast.error("Something went wrong!");
        })
        .finally(() => {
          this.loaders.campaign_list_loader = false;
        });
    },

    toggleAll() {
      if (this.allSelected) {
        this.checkedRows = [];
      } else {
        // Select only active rows (status = 1)
        this.checkedRows = this.campaign_list
          .filter(item => item.status == 1 && item.id)
          .map(item => String(item.id));
      }
    },
    normalizeCampaignItem(item) {
      const editCampaignId =
        item.campaign_id ||
        item.campaignId ||
        item.campaign_uuid ||
        item.id ||
        item._id ||
        item.uuid ||
        null;

      return {
        ...item,
        edit_campaign_id: editCampaignId,
        id:
          item.id ||
          item.campaign_id ||
          item.campaignId ||
          item.campaign_uuid ||
          item._id ||
          item.uuid ||
          null,
      };
    },
    deleteSelectedCampaigns() {
      this.showDeleteDialog = false;

      if (this.checkedRows.length === 0) return;

      this.loaders.campaign_list_loader = true;

      axios.post(
        `${process.env.VUE_APP_API_URL}/api/v4/delete-campaign`,
        { ids: this.checkedRows },
        { headers: this.$headers }
      )
      .then((response) => {
        if (response.data.status === 1) {
          this.snackBar.message = response.data.message;
          this.snackBar.show = true;
          // alert(response.data.message || "campaigns deleted successfully!");
          this.getCampaignList();
          this.checkedRows = [];
        } else {
          // alert(response.data.message || "Failed to delete campaigns.");
          this.snackBar.message = response.data.message;
          this.snackBar.show = true;
        } 
      })
      .catch(() => {
        this.$toast && this.$toast.error("Something went wrong!");
      })
      .finally(() => {
        this.loaders.campaign_list_loader = false;
      });
    },
    getStatusLabel(item) {
      if (item.valid_till < this.current_date) {
        return item.status == 1 ? "Expired" : "Inactive";
      }
      return item.status == 1 ? "Active" : "Inactive";
    },

    getStatusColor(item) {
      if (item.valid_till < this.current_date) {
        return item.status == 1 ? "grey" : "red";
      }
      return item.status == 1 ? "green" : "red";
    },

    copyCampaign(item) {
      const selectedCampaignId = item.edit_campaign_id || item.campaign_id || item.id || null;

      if (!selectedCampaignId) {
        this.snackBar.message = "Campaign ID not found for this row.";
        this.snackBar.color = "error";
        this.snackBar.show = true;
        return;
      }

      this.$emit("copy-campaign", {
        ...item,
        campaign_id: selectedCampaignId,
      });
    },
  },
};
</script>
